import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Instagram } from "lucide-react";

export default function Footer() {
  const { t } = useTranslation();
  const currentYear = new Date().getFullYear();

  const navLinks = [
    { to: "/nosotros", label: t("nav.about") },
    { to: "/soluciones", label: t("nav.solutions") },
    { to: "/portafolio", label: t("nav.portfolio") },
    { to: "/agendar", label: t("nav.schedule") },
    { to: "/soporte", label: t("nav.support") },
  ];

  return (
    <footer className="relative border-t border-[var(--color-border)] bg-[var(--color-bg-primary)]">
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[var(--color-accent)]/35 to-transparent" />

      <div className="mx-auto max-w-6xl px-5 py-16 sm:px-8 lg:px-10 lg:py-20">
        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-12">
          <div className="space-y-4 lg:col-span-5">
            <Link
              to="/"
              className="inline-block text-xl font-bold tracking-tight text-[var(--color-text-primary)]"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              ta<span className="text-[var(--color-accent)]">Code</span>
            </Link>
            <p className="max-w-xs text-sm leading-relaxed text-[var(--color-text-secondary)]">
              {t("footer.tagline")}
            </p>
            <p className="text-xs text-[var(--color-text-secondary)]/60">
              {t("footer.location")}
            </p>
            <a
              href="https://www.instagram.com/_tacode_/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t("footer.instagramLabel")}
              className="inline-flex items-center justify-center rounded-lg border border-[var(--color-border-strong)] p-2 text-[var(--color-text-secondary)] transition-colors hover:border-[var(--color-accent)]/35 hover:text-[var(--color-accent)]"
            >
              <Instagram className="h-4 w-4" />
            </a>
          </div>

          <div className="lg:col-span-3">
            <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--color-text-secondary)]/60">
              {t("footer.pagesHeading")}
            </p>
            <ul className="space-y-3">
              {navLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="space-y-8 lg:col-span-4">
            <div>
              <p className="mb-5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--color-text-secondary)]/60">
                {t("footer.legalHeading")}
              </p>
              <ul className="space-y-3">
                <li>
                  <Link
                    to="/terminos"
                    className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                  >
                    {t("footer.terms")}
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacidad"
                    className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-text-primary)]"
                  >
                    {t("footer.privacy")}
                  </Link>
                </li>
              </ul>
            </div>
            <div>
              <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--color-text-secondary)]/60">
                {t("footer.contactHeading")}
              </p>
              <a
                href="mailto:contacto@tacode.pro"
                className="text-sm text-[var(--color-text-secondary)] transition-colors hover:text-[var(--color-accent)]"
              >
                contacto@tacode.pro
              </a>
            </div>
          </div>
        </div>

        <div className="mt-14 flex flex-col gap-2 border-t border-[var(--color-border)] pt-7 text-xs text-[var(--color-text-secondary)]/50 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {currentYear}{" "}
            <span
              className="font-semibold text-[var(--color-text-secondary)]"
              style={{ fontFamily: "var(--font-heading)" }}
            >
              taCode
            </span>
            . {t("footer.rights")}
          </p>
          <div className="flex gap-5">
            <Link to="/terminos" className="transition-colors hover:text-[var(--color-text-secondary)]">
              {t("footer.termsShort")}
            </Link>
            <Link to="/privacidad" className="transition-colors hover:text-[var(--color-text-secondary)]">
              {t("footer.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
